import React from 'react';
import { Upload as AntUpload, Tooltip } from 'antd';
import { RcFile } from 'antd/es/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import Text from './typography/Text';
import {
  DeleteOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  WarningOutlined,
} from '@ant-design/icons';

type UploadStatus = 'error' | 'success' | 'done' | 'uploading' | 'removed';

interface UploadedFileData {
  name?: string;
  percent?: number;
  status?: UploadStatus;
  fileObj?: File;
}

interface UploadedFile {
  id?: FileId;
  name: FileName;
  percent?: Percentage;
  status?: UploadStatus;
  errorText?: string;
}

interface IUploadedFilesList<File> {
  filesList: File[];
  onRemove?: (file: File) => void;
  hideRemoveAction?: boolean;
}

interface FileUploadProps {
  fileList?: UploadedFileData[];
  onSelect?: (file: File | File[]) => void;
  errorText?: string;
  onRemove?: (fileData: UploadedFileData) => void;
  hideContent?: boolean;
  acceptTypes?: string;
  showUploadedFile?: boolean;
  children?: React.ReactNode;
  multiple?: boolean;
  maxCount?: CountNumber;
}

function FileUpload({
  fileList,
  onSelect,
  onRemove,
  acceptTypes,
  errorText,
  hideContent,
  showUploadedFile,
  children,
  multiple,
  maxCount,
}: FileUploadProps): JSX.Element {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const filesAccumulatorRef = React.useRef<File[]>([]);

  const onAntBeforeUpload = (file: RcFile) => {
    if (!onSelect) return false;

    if (multiple) {
      filesAccumulatorRef.current.push(file);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        onSelect([...filesAccumulatorRef.current]);
        filesAccumulatorRef.current = [];
      }, 100);
    } else {
      onSelect(file);
    }

    return false;
  };

  const antFileList: UploadFile[] | undefined = fileList?.map(file => {
    return {
      uid: '',
      ...file,
    } as UploadFile;
  });

  return (
    <StyledUpload
      maxCount={maxCount}
      multiple={multiple}
      fileList={antFileList}
      onRemove={onRemove}
      beforeUpload={onAntBeforeUpload}
      accept={acceptTypes}
      showUploadList={showUploadedFile}
      itemRender={(originNode, file) => {
        return (
          <>
            {file.status === 'error' ? (
              <Tooltip title={file.response || errorText}>{originNode.props.children}</Tooltip>
            ) : (
              originNode
            )}
          </>
        );
      }}
    >
      {!hideContent ? children : undefined}
    </StyledUpload>
  );
}

function UploadedFilesList<File extends UploadedFile>({
  filesList,
  onRemove,
  hideRemoveAction,
}: IUploadedFilesList<File>): JSX.Element {
  const icon = {
    ['uploading']: <LoadingOutlinedUploadedList />,
    ['success']: <PaperClipIconUploadedList />,
    ['done']: <PaperClipIconUploadedList />,
    ['error']: <WarningOutlinedUploadedList />,
  };

  return (
    <WrapperUploadedList>
      {filesList.map((file, index) => (
        <FileWrapperUploadedList key={index}>
          <FileContainerUploadedList>
            <IconStatusWrapper>{icon[file.status || 'success']}</IconStatusWrapper>
            {file.status === 'error' && file.errorText ? (
              <Tooltip title={file.errorText}>
                <StyledTextUploadedList status={file.status}>{file.name}</StyledTextUploadedList>
              </Tooltip>
            ) : (
              <StyledTextUploadedList status={file.status}>{file.name}</StyledTextUploadedList>
            )}
          </FileContainerUploadedList>
          {!hideRemoveAction && (
            <ActionContainerUploadedList>
              <DeleteIconUploadedList
                onClick={e => {
                  onRemove && onRemove(file);
                }}
              />
            </ActionContainerUploadedList>
          )}
          {file.status === 'uploading' && (
            <ProgressBarWrapper>
              <ProgressBarInner>
                <ProgressStyled style={{ width: `${file.percent}%` }} />
              </ProgressBarInner>
            </ProgressBarWrapper>
          )}
        </FileWrapperUploadedList>
      ))}
    </WrapperUploadedList>
  );
}

const ActionContainerUploadedList = styled.div``;

const WrapperUploadedList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  position: relative;
`;

const FileWrapperUploadedList = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  & ${ActionContainerUploadedList} {
    visibility: hidden;
  }

  &:hover ${ActionContainerUploadedList} {
    visibility: visible;
  }
`;

const FileContainerUploadedList = styled.div<{ status?: UploadStatus }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;
const IconStatusWrapper = styled.div``;

const StyledTextUploadedList = styled(Text)<{ status?: UploadStatus }>`
  padding: 0 8px;
  color: ${props => (props.status === 'error' ? colorsPalette.red500 : colorsPalette.dustyBlue500)};
`;

const PaperClipIconUploadedList = styled(PaperClipOutlined)`
  color: ${colorsPalette.gray100};
  font-size: 14px;
`;

const LoadingOutlinedUploadedList = styled(LoadingOutlined)`
  color: ${colorsPalette.gray100};
  font-size: 14px;
`;

const WarningOutlinedUploadedList = styled(WarningOutlined)`
  color: ${colorsPalette.red500};
  font-size: 14px;
`;

const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding-inline-start: 22px;
  font-size: 14px;
  line-height: 0;
  pointer-events: none;
`;

const ProgressBarInner = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  overflow: hidden;
  vertical-align: middle;
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: 100px;
`;

const ProgressStyled = styled.div`
  background-color: ${colorsPalette.dustyBlue500};
  height: 2px;
  position: relative;
  border-radius: 100px;
  transition: all 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
`;

const DeleteIconUploadedList = styled(DeleteOutlined)`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
`;

const StyledUpload = styled(AntUpload)`
  button {
    border-style: dashed;
    color: #404040;
    padding: 14px 16px;
  }
  & {
    .ant-upload-list-item-container {
      transition: none !important;
    }

    .ant-upload-list-item {
      margin-top: 0 !important;
    }

    .ant-upload-list-item-name {
      color: ${colorsPalette.dustyBlue500};
    }

    .ant-upload-list-item-error {
      .ant-upload-list-item-name {
        color: red;
      }
    }

    .ant-progress .ant-progress-bg {
      background-color: ${colorsPalette.dustyBlue500} !important;
    }
  }
`;

FileUpload.UploadedFilesList = UploadedFilesList;

export { FileUpload, type UploadStatus, type UploadedFileData };
